import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment-timezone';

import { Descriptions, Tabs } from 'antd';
import {
  CaretUpFilled,
  BarsOutlined,
  ThunderboltFilled,
  ToolFilled,
} from '@ant-design/icons';

import { ReactorType } from '../../../utils/types';
import Link from '../../../components/Link';

import ProductionTab from './ProductionTab';
import UnavailabilitiesTab from './UnavailabilitiesTab';

function ReactorDetails({
  reactor,
  currentDate,
  currentTab: tabInput,
  setTab,
}) {
  // Handle case when reactor data is not yet loaded or doesn't exist
  if (!reactor) {
    return (
      <div className="ReactorDetails">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Loading reactor details...
        </div>
      </div>
    );
  }

  const currentTab =
    tabInput ||
    (reactor.status === 'RUNNING' ? 'production' : 'unavailabilities');

  return (
    <div className="ReactorDetails">
      <div className="ReactorDetails__header">
        <div className="ReactorDetails__headerLine">
          <div className="ReactorDetails__headerLine__left">
            <div className="ReactorDetails__headerLine__title">
              {reactor.name}
            </div>
            <div>{`(${reactor.netPower_MW} MWe)`}</div>
          </div>

          <div>
            <Link to={`/plant/${reactor.plantId}`}>
              <CaretUpFilled />
            </Link>
          </div>
        </div>

        <Tabs
          activeKey={currentTab}
          type="card"
          onChange={setTab}
          items={[
            {
              key: 'info',
              label: <BarsOutlined />,
            },
            {
              key: 'production',
              label: <ThunderboltFilled />,
            },
            {
              key: 'unavailabilities',
              label: <ToolFilled />,
            },
          ]}
        />
      </div>

      <Tabs
        activeKey={currentTab}
        onChange={setTab}
        renderTabBar={() => <div />}
        items={[
          {
            key: 'info',
            label: '',
            children: (
              <Descriptions size="small" bordered column={1}>
                <Descriptions.Item label="Palier">
                  {reactor.stage}
                </Descriptions.Item>
                <Descriptions.Item label="Puissance thermique">
                  {`${reactor.thermalPower_MW} MW`}
                </Descriptions.Item>
                <Descriptions.Item label="Puissance électrique brute">
                  {`${reactor.rawPower_MW} MW`}
                </Descriptions.Item>
                <Descriptions.Item label="Puissance électrique nette">
                  {`${reactor.netPower_MW} MW`}
                </Descriptions.Item>
                <Descriptions.Item label="Aéroréfrigérants">
                  {reactor.coolingTowerCount}
                </Descriptions.Item>
                <Descriptions.Item label="Début de construction">
                  {reactor.buildStartDate}
                </Descriptions.Item>
                <Descriptions.Item label="Divergence">
                  {reactor.firstReactionDate}
                </Descriptions.Item>
                <Descriptions.Item label="Raccordement au réseau">
                  {reactor.gridLinkDate}
                </Descriptions.Item>
                <Descriptions.Item label="Début d'exploitation">
                  {reactor.exploitationStartDate}
                  <br />
                  <strong>
                    {` (${moment().diff(
                      moment(reactor.exploitationStartDate, 'DD/MM/YYYY'),
                      'years',
                    )} ans)`}
                  </strong>
                </Descriptions.Item>
                {reactor.moxAuthorizationDate && (
                  <Descriptions.Item label="Autorisation MOX">
                    {reactor.moxAuthorizationDate}
                  </Descriptions.Item>
                )}
              </Descriptions>
            ),
          },
          {
            key: 'production',
            label: '',
            children: (
              <ProductionTab currentDate={currentDate} reactor={reactor} />
            ),
          },
          {
            key: 'unavailabilities',
            label: '',
            children: (
              <UnavailabilitiesTab
                reactor={reactor}
                currentDate={currentDate}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

ReactorDetails.propTypes = {
  reactor: ReactorType,
  currentDate: PropTypes.string.isRequired,
  currentTab: PropTypes.string.isRequired,
  setTab: PropTypes.func.isRequired,
};

export default ReactorDetails;
